import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Lock, Play, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const T = {
  fr: { title: "Découvrez Anou Appran en 2 minutes", sub: "Tout ce que votre enfant va vivre : inscription, matières, récompenses, kit Lecture, progrès et paiement.",
    must: "Regardez la vidéo jusqu'au bout pour continuer.", go: "Continuer", watched: "Vidéo terminée !" },
  en: { title: "Discover Anou Appran in 2 minutes", sub: "Everything your child will enjoy: sign up, subjects, rewards, Reading kit, progress and payment.",
    must: "Watch the video to the end to continue.", go: "Continue", watched: "Video finished!" },
};
const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export function useIntroMeta(lang) {
  const [meta, setMeta] = useState(null);
  useEffect(() => { fetch(`/intro/intro_${lang}.json?v=4`).then((r) => r.json()).then(setMeta).catch(() => setMeta(null)); }, [lang]);
  return meta;
}

// Parent intro video. mandatory: no skipping ahead, Continue unlocks at the end.
export function IntroVideoPlayer({ lang, mandatory, onEnded, autoPlay = false }) {
  const v = useRef(null);
  const meta = useIntroMeta(lang);
  const [t, setT] = useState(0);
  const [max, setMax] = useState(0);
  const [playing, setPlaying] = useState(false);
  useEffect(() => { setT(0); setMax(0); }, [lang]);
  const dur = meta?.duration || 1;
  const onTime = () => {
    const now = v.current.currentTime;
    if (mandatory && now > max + 1.5) { v.current.currentTime = max; return; }
    setT(now);
    setMax((m) => Math.max(m, now));
  };
  const jump = (s) => { if (!mandatory || s <= max + 1) { v.current.currentTime = s; v.current.play()?.catch(() => {}); } };
  return (
    <div data-testid="intro-video-player">
      <div className="relative rounded-[1.6rem] border-4 border-[#111] bg-[#111] overflow-hidden shadow-[4px_4px_0_0_#111] sm:shadow-[8px_8px_0_0_#111]">
        <video ref={v} key={lang} src={`/intro/intro_${lang}.mp4?v=4`} poster={`/intro/intro_${lang}.jpg?v=4`} playsInline autoPlay={autoPlay}
          controls={!mandatory} className="w-full aspect-video bg-[#FDFCF0]" data-testid="intro-video"
          onTimeUpdate={onTime} onSeeking={onTime} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
          onEnded={() => { setMax(dur); onEnded?.(); }} />
        {mandatory && !playing && (
          <button onClick={() => v.current.play()?.catch(() => {})} data-testid="intro-video-play"
            className="absolute inset-0 m-auto w-20 h-20 rounded-full bg-[#B7F000] border-4 border-[#111] shadow-[4px_4px_0_0_#111] flex items-center justify-center hover:scale-105 transition-transform">
            <Play size={34} fill="#111" />
          </button>
        )}
        {mandatory && playing && <button onClick={() => v.current.pause()} className="absolute inset-0" aria-label="pause" data-testid="intro-video-pause" />}
      </div>
      <div className="mt-4 h-3 rounded-full border-2 border-[#111] bg-white overflow-hidden" data-testid="intro-video-progress">
        <div className="h-full bg-[#B7F000] transition-[width] duration-300" style={{ width: `${Math.min(100, (t / dur) * 100)}%` }} />
      </div>
      <p className="mt-1 text-xs font-bold text-slate-600 tabular-nums" data-testid="intro-video-time">{fmt(t)} / {fmt(dur)}</p>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="intro-video-chapters">
        {(meta?.chapters || []).map((c, i) => {
          const open = !mandatory || c.start <= max + 1;
          const active = t >= c.start && t < (meta.chapters[i + 1]?.start ?? 1e9);
          return (
            <button key={c.label} onClick={() => jump(c.start)} disabled={!open} data-testid={`intro-chapter-${i}`}
              className={`rounded-full border-2 border-[#111] px-3 py-1 text-xs font-bold inline-flex items-center gap-1 transition-transform hover:-translate-y-0.5 ${active ? "bg-[#FFDF00]" : open ? "bg-white" : "bg-slate-100 text-slate-400 border-slate-300"}`}>
              {!open && <Lock size={11} />} {c.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const LangToggle = ({ lang, setLang }) => (
  <div className="inline-flex rounded-full border-[3px] border-[#111] bg-white p-1" data-testid="intro-video-lang-toggle">
    {["fr", "en"].map((l) => (
      <button key={l} onClick={() => setLang(l)} data-testid={`intro-lang-${l}`}
        className={`rounded-full px-4 py-1 text-sm font-black ${lang === l ? "bg-[#111] text-white" : "text-[#111]"}`}>{l === "fr" ? "Français" : "English"}</button>
    ))}
  </div>
);

export default function IntroVideoModal({ mandatory = true, onClose }) {
  const { refresh } = useAuth();
  const [lang, setLang] = useState("fr");
  const [done, setDone] = useState(false);
  const w = T[lang];
  useEffect(() => { toast.dismiss(); }, []);
  const finish = async () => {
    await api.post("/auth/intro-seen").catch(() => {});
    await refresh();
    onClose?.();
  };
  return (
    <div className="fixed inset-0 z-[60] bg-[#0F172A]/80 backdrop-blur-md overflow-y-auto p-4 sm:p-8" data-testid="intro-video-modal">
      <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        className="relative max-w-4xl mx-auto bg-[#FDFCF0] border-4 border-[#111] rounded-[2rem] shadow-[10px_10px_0_0_#111] p-5 sm:p-8">
        {!mandatory && (
          <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full border-[3px] border-[#111] bg-white flex items-center justify-center" data-testid="intro-video-close"><X size={18} /></button>
        )}
        <div className="flex flex-wrap items-start gap-4 justify-between pr-12">
          <div className="flex items-center gap-3">
            <img src="/mascot/kiko.png" alt="Kiko" className="w-14 h-14 object-contain" />
            <div>
              <h2 className="font-kids text-2xl sm:text-3xl font-black tracking-tight" data-testid="intro-video-title">{w.title}</h2>
              <p className="text-sm sm:text-base text-slate-700 mt-1 max-w-xl">{w.sub}</p>
            </div>
          </div>
          <LangToggle lang={lang} setLang={(l) => { setLang(l); setDone(false); }} />
        </div>
        <div className="mt-6"><IntroVideoPlayer lang={lang} mandatory={mandatory} onEnded={() => setDone(true)} /></div>
        <div className="mt-6 flex flex-wrap items-center gap-4 justify-between">
          <p className={`text-sm font-bold ${done ? "text-green-700" : "text-slate-600"}`} data-testid="intro-video-status">
            {done ? <><Check className="inline -mt-1" size={16} /> {w.watched}</> : mandatory ? w.must : ""}
          </p>
          <button onClick={finish} disabled={mandatory && !done} data-testid="intro-video-continue-button"
            className="rounded-full border-[3px] border-[#111] bg-[#B7F000] px-8 py-3 font-kids font-black text-lg shadow-[4px_4px_0_0_#111] hover:translate-y-[2px] hover:shadow-[2px_2px_0_0_#111] transition-transform transition-shadow disabled:opacity-40 disabled:pointer-events-none">
            {w.go}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
